import { useState, useEffect, useCallback } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { fetchWithAuth } from '../libs/apiUtils'
import { 
  MagnifyingGlassIcon, 
  FolderIcon, 
  TagIcon, 
  TableCellsIcon, 
  ChevronRightIcon,
  CalendarIcon,
  FunnelIcon,
  XMarkIcon
} from '@heroicons/react/24/outline'
import Pagination from '../components/Pagination'

interface SearchItem {
  id: string
  name: string
  type: 'group' | 'category' | 'table'
  groupId?: string
  categoryId?: string
  groupName?: string
  categoryName?: string
  isPublic?: boolean
  tableCount?: number
  createdByName?: string
  createdAt?: string
}

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()

  const initialQuery = searchParams.get('q') || ''
  const [query, setQuery] = useState(initialQuery)
  const [typeFilter, setTypeFilter] = useState<'all' | 'group' | 'category' | 'table'>('all')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')

  const [items, setItems] = useState<SearchItem[]>([])
  const [loading, setLoading] = useState(false)

  // Pagination state
  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState(10)
  const [totalItems, setTotalItems] = useState(0)

  // Keep search params in sync
  useEffect(() => {
    const q = searchParams.get('q') || ''
    setQuery(q)
  }, [searchParams])

  const performSearch = useCallback(async () => {
    if (!query.trim()) {
      setItems([])
      setTotalItems(0)
      return
    }

    setLoading(true)
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL
      const searchStr = query.trim()

      const fetchGroups = typeFilter === 'all' || typeFilter === 'group'
      const fetchCategories = typeFilter === 'all' || typeFilter === 'category'
      const fetchTables = typeFilter === 'all' || typeFilter === 'table'

      const promises: Promise<Response>[] = []

      if (fetchGroups) {
        promises.push(
          fetchWithAuth(`${backendUrl}/api/groups`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ page: 1, perPage: 100, search: searchStr, startDate: startDate || undefined, endDate: endDate || undefined }),
          })
        )
      } else {
        promises.push(Promise.resolve(new Response(JSON.stringify({ data: [], meta: { total: 0 } }))))
      }

      if (fetchCategories) {
        promises.push(
          fetchWithAuth(`${backendUrl}/api/categories`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ page: 1, perPage: 100, search: searchStr, startDate: startDate || undefined, endDate: endDate || undefined }),
          })
        )
      } else {
        promises.push(Promise.resolve(new Response(JSON.stringify({ data: [], meta: { total: 0 } }))))
      }

      if (fetchTables) {
        promises.push(
          fetchWithAuth(`${backendUrl}/api/tables`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ page: 1, perPage: 100, search: searchStr, startDate: startDate || undefined, endDate: endDate || undefined }),
          })
        )
      } else {
        promises.push(Promise.resolve(new Response(JSON.stringify({ data: [], meta: { total: 0 } }))))
      }

      const [groupsRes, categoriesRes, tablesRes] = await Promise.all(promises)
      const [groupsData, categoriesData, tablesData] = await Promise.all([
        groupsRes.json(),
        categoriesRes.json(),
        tablesRes.json()
      ])

      const combined: SearchItem[] = [
        ...(groupsData.data || []).map((g: any) => ({
          id: g.id,
          name: g.name,
          type: 'group' as const,
          tableCount: g.tableCount,
          createdByName: g.createdByName,
          createdAt: g.createdAt
        })),
        ...(categoriesData.data || []).map((c: any) => ({
          id: c.id,
          name: c.name,
          type: 'category' as const,
          groupId: c.groupId,
          groupName: c.groupName,
          tableCount: c.tableCount,
          createdByName: c.createdByName,
          createdAt: c.createdAt
        })),
        ...(tablesData.data || []).map((t: any) => ({
          id: t.id,
          name: t.name,
          type: 'table' as const,
          categoryId: t.categoryId,
          categoryName: t.categoryName,
          groupId: t.groupId,
          groupName: t.groupName,
          isPublic: t.isPublic,
          createdByName: t.createdByName,
          createdAt: t.createdAt
        }))
      ]

      setTotalItems(combined.length)

      // Paginate client-side over combined set
      const startIndex = (page - 1) * perPage
      const paginated = combined.slice(startIndex, startIndex + perPage)
      setItems(paginated)
    } catch (err) {
      console.error('Failed search query', err)
    } finally {
      setLoading(false)
    }
  }, [query, typeFilter, startDate, endDate, page, perPage])

  useEffect(() => {
    performSearch()
  }, [performSearch])

  function handleSearchSubmit(e: React.FormEvent) {
    e.preventDefault()
    setPage(1)
    setSearchParams({ q: query })
  }

  const roleSlug = localStorage.getItem('user_role_slug')
  const isConsumer = roleSlug === 'consumer' || roleSlug === 'user'

  function handleItemClick(item: SearchItem) {
    if (item.type === 'group') {
      navigate(`/data/${item.id}/category`)
    } else if (item.type === 'category') {
      if (item.groupId) {
        navigate(`/data/${item.groupId}/${item.id}/table`)
      } else {
        navigate(`/data/group`)
      }
    } else if (item.type === 'table') {
      if (isConsumer) {
        navigate(`/data/${item.id}`)
      } else if (item.groupId && item.categoryId) {
        navigate(`/data/${item.groupId}/${item.categoryId}/${item.id}`)
      } else {
        navigate(`/data/group`)
      }
    }
  }

  const lastPage = Math.ceil(totalItems / perPage) || 1

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-extrabold text-neutral-900 tracking-tight mb-2">Pencarian Manajemen</h1>
        <p className="text-sm text-neutral-500">Cari dan jelajahi Grup, Kategori, dan Tabel di seluruh sistem.</p>
      </div>

      {/* Main Search Bar and Filters */}
      <div className="bg-white rounded-2xl p-6 shadow-xl shadow-neutral-100 border border-neutral-100 mb-8 space-y-4">
        <form onSubmit={handleSearchSubmit} className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="size-5 absolute left-3.5 top-1/2 -translate-y-1/2 text-neutral-400" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Masukkan kata kunci pencarian..."
              className="w-full pl-11 pr-4 py-3 bg-neutral-50 border border-neutral-200 rounded-xl text-sm text-neutral-800 placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all font-medium"
            />
            {query && (
              <button
                type="button"
                onClick={() => { setQuery(''); setSearchParams({}) }}
                className="absolute right-3.5 top-1/2 -translate-y-1/2 text-xs font-semibold text-neutral-400 hover:text-neutral-600 bg-neutral-200 hover:bg-neutral-300 rounded-full px-1.5 py-0.5"
              >
                ✕
              </button>
            )}
          </div>
          <button
            type="submit"
            className="flex items-center justify-center gap-2 bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-xl font-bold shadow-lg shadow-primary-200 transition-all active:scale-95 whitespace-nowrap"
          >
            <MagnifyingGlassIcon className="size-5" />
            Cari
          </button>
        </form>

        {/* Filters */}
        <div className="flex flex-wrap items-center justify-between gap-4 pt-4 border-t border-neutral-100">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-bold text-neutral-500 uppercase tracking-wider flex items-center gap-1 mr-2">
              <FunnelIcon className="size-4 text-neutral-400" />
              Tipe:
            </span>
            {(['all', 'group', 'category', 'table'] as const).map((t) => (
              <button
                key={t}
                onClick={() => { setTypeFilter(t); setPage(1) }}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                  typeFilter === t
                    ? 'bg-primary-600 text-white shadow-md shadow-primary-200'
                    : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
                }`}
              >
                {t === 'all' && 'Semua Tipe'}
                {t === 'group' && 'Grup'}
                {t === 'category' && 'Kategori'}
                {t === 'table' && 'Tabel'}
              </button>
            ))}
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <CalendarIcon className="size-4 text-neutral-400" />
              <input
                type="date"
                value={startDate}
                onChange={(e) => { setStartDate(e.target.value); setPage(1) }}
                className="bg-neutral-50 border border-neutral-200 rounded-lg px-2.5 py-1 text-xs text-neutral-700 focus:outline-none focus:ring-1 focus:ring-primary-500 font-medium"
              />
              <span className="text-xs text-neutral-400">s/d</span>
              <input
                type="date"
                value={endDate}
                onChange={(e) => { setEndDate(e.target.value); setPage(1) }}
                className="bg-neutral-50 border border-neutral-200 rounded-lg px-2.5 py-1 text-xs text-neutral-700 focus:outline-none focus:ring-1 focus:ring-primary-500 font-medium"
              />
            </div>
            {(startDate || endDate) && (
              <button
                onClick={() => { setStartDate(''); setEndDate(''); setPage(1) }}
                className="p-1 text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 rounded-md transition-colors"
                title="Reset Tanggal"
              >
                <XMarkIcon className="size-4" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Results List */}
      <div className="bg-white rounded-2xl shadow-xl shadow-neutral-100 border border-neutral-100 overflow-hidden">
        {loading ? (
          <div className="p-20 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-4 text-neutral-500 font-medium">Mencari data...</p>
          </div>
        ) : items.length === 0 ? (
          <div className="p-20 text-center">
            <div className="bg-neutral-100 size-16 rounded-full flex items-center justify-center mx-auto mb-4 text-neutral-400">
              <MagnifyingGlassIcon className="size-8" />
            </div>
            <h3 className="text-lg font-bold text-neutral-800">Tidak Ada Hasil Ditemukan</h3>
            <p className="text-neutral-500 max-w-xs mx-auto mt-2 text-sm">
              Coba sesuaikan kata kunci atau atur ulang filter tipe dan tanggal.
            </p>
          </div>
        ) : (
          <div className="divide-y divide-neutral-100">
            {items.map((item) => (
              <div
                key={`${item.type}-${item.id}`}
                onClick={() => handleItemClick(item)}
                className="p-5 hover:bg-primary-50/40 transition-colors cursor-pointer flex flex-col sm:flex-row sm:items-center justify-between gap-4 group"
              >
                <div className="flex items-start gap-4">
                  <div className="mt-1">
                    {item.type === 'group' && (
                      <span className="inline-flex items-center gap-1 text-xs font-black px-3 py-1 rounded-full bg-blue-50 text-blue-700 border border-blue-100 shadow-sm">
                        <FolderIcon className="size-4" />
                        GRUP
                      </span>
                    )}
                    {item.type === 'category' && (
                      <span className="inline-flex items-center gap-1 text-xs font-black px-3 py-1 rounded-full bg-purple-50 text-purple-700 border border-purple-100 shadow-sm">
                        <TagIcon className="size-4" />
                        KATEGORI
                      </span>
                    )}
                    {item.type === 'table' && (
                      <span className="inline-flex items-center gap-1 text-xs font-black px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-100 shadow-sm">
                        <TableCellsIcon className="size-4" />
                        TABEL
                      </span>
                    )}
                  </div>

                  <div>
                    <h4 className="text-base font-bold text-neutral-900 group-hover:text-primary-600 transition-colors">
                      {item.name}
                    </h4>

                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-neutral-500 mt-1 font-medium">
                      {item.groupName && (
                        <span>
                          Grup: <strong className="text-neutral-700">{item.groupName}</strong>
                        </span>
                      )}
                      {item.categoryName && (
                        <span>
                          Kategori: <strong className="text-neutral-700">{item.categoryName}</strong>
                        </span>
                      )}
                      {item.tableCount !== undefined && (
                        <span>Total Tabel: <strong>{item.tableCount}</strong></span>
                      )}
                      {item.createdByName && (
                        <span>Dibuat Oleh: <strong>{item.createdByName}</strong></span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  {item.isPublic !== undefined && (
                    <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${
                      item.isPublic ? 'bg-green-50 text-green-700' : 'bg-yellow-50 text-yellow-700'
                    }`}>
                      {item.isPublic ? 'Publik' : 'Privat'}
                    </span>
                  )}
                  <div className="size-8 rounded-full bg-neutral-100 group-hover:bg-primary-600 group-hover:text-white flex items-center justify-center transition-colors text-neutral-400">
                    <ChevronRightIcon className="size-5" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {totalItems > 0 && (
          <div className="px-6 py-4 bg-neutral-50/50 border-t border-neutral-100">
            <Pagination
              currentPage={page}
              lastPage={lastPage}
              total={totalItems}
              perPage={perPage}
              onPageChange={setPage}
              onPerPageChange={(newPerPage) => {
                setPerPage(newPerPage)
                setPage(1)
              }}
            />
          </div>
        )}
      </div>
    </div>
  )
}
